import { useCallback, useEffect, useRef, useState } from "react";
import { toPublicStorageUrl } from "../config/imageConfig";
import { getUserProfile } from "../utils/auth";
import { VIRTUAL_TRY_ON_URL } from "../config/apiConfig";
import {
  notifyVirtualTryOnImageLoaded,
  notifyVirtualTryOnFailed,
  notifyVirtualTryOnClosed,
} from "../utils/virtualTryOnAvatarHold";
import { trackVtoChanged } from "../services/uiAgentSync";

/**
 * The virtual try-on state machine, shared by the inline mirror on product
 * pages and the fallback popup (VirtualTryOn.js). One implementation of the
 * API call, the result parsing and the side effects (agent sync, avatar mic
 * hold), so the two surfaces cannot drift apart.
 *
 * Steps: 1 choose a model · 2 rendering · 3 result.
 *
 * @param {object}  opts
 * @param {boolean} opts.open       The surface is showing (drives sync + auto-start).
 * @param {string}  opts.articleId  Product to render.
 * @param {string|null} [opts.autoStart]  "custom_model" | "default" | null —
 *   render straight away in this mode instead of showing the picker (the
 *   agent's "how does this look on me?" path).
 * @param {(msg: string) => void} [opts.onError]  Called with a human message
 *   on failure; the hook also exposes `error` for inline display.
 */
export default function useVirtualTryOn({ open, articleId, autoStart = null, onError }) {
  const [step, setStep] = useState(1);
  const [personImage, setPersonImage] = useState(null);
  const [personImagePreview, setPersonImagePreview] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  /** Latches so an auto-start fires once per open, not on every re-render. */
  const autoStartedRef = useRef(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // UI → Agent sync: report the try-on opening/closing.
  useEffect(() => {
    if (!open) return undefined;
    trackVtoChanged(true, articleId);
    return () => trackVtoChanged(false, articleId);
  }, [open, articleId]);

  // If the surface unmounts mid-session (route change), release the avatar
  // mic hold. The release is idempotent, so a prior explicit close is fine.
  useEffect(() => () => notifyVirtualTryOnClosed(), []);

  const fail = useCallback((message) => {
    setError(message);
    if (onErrorRef.current) onErrorRef.current(message);
  }, []);

  const start = useCallback(
    async (mode = "default", imageBase64 = null) => {
      if (!articleId) {
        fail("This product can't be tried on right now.");
        return;
      }
      if (mode === "user_upload" && !personImage && !imageBase64) {
        fail("Please choose a photo first.");
        return;
      }

      setError("");
      setStep(2);

      try {
        const userProfile = getUserProfile();
        const customerName = userProfile
          ? formatCustomModelName(
              userProfile.username ||
                userProfile.customer_name ||
                userProfile.name ||
                userProfile.customer_id ||
                userProfile.first_name
            )
          : null;

        const payload = { article_id: articleId };
        if (mode === "custom_model") {
          if (!customerName) {
            setStep(1);
            fail("Sign in to try it on with your own profile picture.");
            return;
          }
          payload.custom_model = customerName;
        } else if (mode === "user_upload") {
          payload.mode = "user_upload";
          payload.person_image = imageBase64 || personImage;
        }
        // "default": article_id only.

        const response = await fetch(VIRTUAL_TRY_ON_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error("API Error Response:", errorText);
          throw new Error(`Try-on failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const url = pickResultImage(data);
        if (!url) {
          throw new Error(
            'No image result found in API response. Expected format: { available: true, source: "generated", image_url: "" }'
          );
        }
        setResultImage(url);
        setImageError(false);
        setStep(3);
      } catch (err) {
        console.error("Try-on error:", err);
        notifyVirtualTryOnFailed();
        setStep(1);
        fail(err.message || "Failed to process try-on. Please try again.");
      }
    },
    [articleId, personImage, fail]
  );

  const handleFileSelect = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        fail("That photo is over 10MB — please choose a smaller one.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        setPersonImage(base64);
        setPersonImagePreview(reader.result);
        // Render as soon as the photo is in — no second click needed.
        start("user_upload", base64);
      };
      reader.readAsDataURL(file);
    },
    [start, fail]
  );

  // Agent-driven try-on: render immediately instead of showing the picker.
  useEffect(() => {
    if (!open) {
      autoStartedRef.current = false; // re-arm for the next open
      return;
    }
    if (!autoStart || !articleId || autoStartedRef.current) return;
    autoStartedRef.current = true;
    start(autoStart);
    // `start` changes identity with `personImage`, which an auto-start never
    // uses; the latch guarantees a single run per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoStart, articleId]);

  const reset = useCallback(() => {
    notifyVirtualTryOnClosed();
    setStep(1);
    setPersonImage(null);
    setPersonImagePreview(null);
    setResultImage(null);
    setImageError(false);
    setShowUploadArea(false);
    setError("");
  }, []);

  const tryAnother = useCallback(() => {
    setStep(1);
    setPersonImage(null);
    setPersonImagePreview(null);
    setResultImage(null);
    setError("");
  }, []);

  /** Forget the uploaded photo (before choosing another). */
  const clearPhoto = useCallback(() => {
    setPersonImage(null);
    setPersonImagePreview(null);
  }, []);

  /** Back to the picker, keeping the uploaded photo so it needn't be re-chosen. */
  const backToUpload = useCallback(() => {
    setStep(1);
    setResultImage(null);
    setShowUploadArea(false);
  }, []);

  const download = useCallback(() => {
    if (!resultImage) return;
    const link = document.createElement("a");
    link.href = resultImage;
    link.download = "virtual-try-on-result.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [resultImage]);

  const onResultLoaded = useCallback(() => {
    setImageError(false);
    notifyVirtualTryOnImageLoaded();
  }, []);

  const onResultError = useCallback(() => {
    console.error("Failed to load result image:", resultImage);
    setImageError(true);
    notifyVirtualTryOnFailed();
  }, [resultImage]);

  /** Re-fetch the result image after a load failure. */
  const retryImage = useCallback(() => {
    if (!resultImage) return;
    setImageError(false);
    const img = new Image();
    img.onload = () => {
      setImageError(false);
      notifyVirtualTryOnImageLoaded();
    };
    img.onerror = () => {
      setImageError(true);
      notifyVirtualTryOnFailed();
    };
    img.src = resultImage;
  }, [resultImage]);

  return {
    step,
    stepLabel: ["Choose a model", "Rendering", "Your try-on"][step - 1],
    personImagePreview,
    resultImage,
    imageError,
    error,
    showUploadArea,
    setShowUploadArea,
    fileInputRef,
    handleFileSelect,
    start,
    reset,
    tryAnother,
    backToUpload,
    clearPhoto,
    download,
    onResultLoaded,
    onResultError,
    retryImage,
  };
}

/** Local part of an email for custom_model (emily.parker@x.com → emily.parker). */
function formatCustomModelName(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const at = raw.indexOf("@");
  const base = at > 0 ? raw.slice(0, at) : raw;
  return base.trim() || null;
}

/** Fix Google Cloud Storage links that come back as console URLs. */
function cleanImageUrl(url) {
  if (!url || typeof url !== "string") return url;
  return toPublicStorageUrl(url.trim());
}

/** A bare base64 string, a data URL or an http(s) URL → something an <img> can show. */
function toDisplayUrl(value) {
  if (!value || typeof value !== "string") return null;
  if (value.startsWith("data:")) return value;
  if (value.startsWith("http://") || value.startsWith("https://")) return cleanImageUrl(value);
  return `data:image/png;base64,${value}`;
}

/**
 * The API has answered in several shapes over time:
 *   user_upload → { available, source, image_base64 }
 *   default     → { available, source, image_url }
 * plus older `images[0]`, `result_image`, `result`, `image` and `url`.
 */
function pickResultImage(data) {
  if (!data) return null;
  if (data.image_base64) return toDisplayUrl(data.image_base64);
  if (data.image_url) return cleanImageUrl(data.image_url);
  if (Array.isArray(data.images) && data.images.length > 0) return toDisplayUrl(data.images[0]);
  if (data.result_image) return toDisplayUrl(data.result_image);
  if (data.result) return toDisplayUrl(data.result);
  if (data.image) return toDisplayUrl(data.image);
  if (data.url) return cleanImageUrl(data.url);
  return null;
}
